import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from './error-handler.service';
import { CompanyService } from './company.service';
import { IIntegrationsQueryParams, IIntegrationsCountQueryParams, ICreateAuthProviderBody } from '../../shared/models/integrations.model';

@Injectable({
  providedIn: 'root'
})
export class IntegrationsService {
  private readonly baseUrl = environment.baseBackendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private companyService: CompanyService
  ) {}

  /**
   * Obtiene DataSourceProviders con parámetros de consulta OData
   */
  getDataSourceProviders(params?: IIntegrationsQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/DataSourceProviders`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuevo DataSourceProvider
   */
  createDataSourceProvider(body: ICreateAuthProviderBody): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/odata/DataSourceProviders`, body, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el conteo de DataSourceProviders
   */
  getDataSourceProvidersCount(params?: IIntegrationsCountQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/DataSourceProviders/$count`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene un DataSourceProvider individual por clave
   * @param key ID del DataSourceProvider
   * @param params Parámetros de consulta opcionales
   */
  getDataSourceProviderByKey(key: number | string, params?: any): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/DataSourceProviders(${key})`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza parcialmente un DataSourceProvider por clave (PATCH OData)
   * @param key ID del DataSourceProvider a actualizar
   * @param data Datos a actualizar
   */
  patchDataSourceProviderByKey(key: number | string, data: Partial<ICreateAuthProviderBody>): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/odata/DataSourceProviders(${key})`, data, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un DataSourceProvider por clave (DELETE OData)
   * @param key ID del DataSourceProvider a eliminar
   */
  deleteDataSourceProviderByKey(key: number | string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/DataSourceProviders(${key})`, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea o reemplaza una relación específica de un DataSourceProvider (PUT $ref)
   * @param key ID del DataSourceProvider
   * @param navigationProperty Propiedad de navegación
   * @param relatedKey ID del recurso relacionado
   */
  putDataSourceProviderRef(key: number | string, navigationProperty: string, relatedKey: number | string): Observable<any> {
    const url = `${this.baseUrl}/odata/DataSourceProviders/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.put<any>(url, {}, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina una relación específica de un DataSourceProvider (DELETE $ref)
   * @param key ID del DataSourceProvider
   * @param navigationProperty Propiedad de navegación
   * @param relatedKey ID del recurso relacionado
   */
  deleteDataSourceProviderRef(key: number | string, navigationProperty: string, relatedKey: number | string): Observable<any> {
    const url = `${this.baseUrl}/odata/DataSourceProviders/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.delete<any>(url, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea una nueva relación específica de un DataSourceProvider (POST $ref)
   * @param key ID del DataSourceProvider
   * @param navigationProperty Propiedad de navegación
   * @param relatedKey ID del recurso relacionado
   */
  postDataSourceProviderRef(key: number | string, navigationProperty: string, relatedKey: number | string): Observable<any> {
    const url = `${this.baseUrl}/odata/DataSourceProviders/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.post<any>(url, {}, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Verifica el estado de las credenciales de un proveedor de datos
   * @param dataSourceProviderId ID del proveedor de datos
   */
  checkCredentialsStatus(dataSourceProviderId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/Credentials/status`, {
      params: { dataSourceProviderId: dataSourceProviderId.toString() }
    });
  }

  /**
   * Crea nuevas credenciales para un proveedor de datos
   * @param credentialsData Datos de las credenciales a crear
   */
  createCredentials(credentialsData: {
    ownerId: number;
    dataSourceProviderId: number;
    payload: any;
    authorized: boolean;
    authorizedAt: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/Credentials`, credentialsData).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Construye los headers HTTP incluyendo el X-Company-Id
   */
  private buildHeaders(): HttpHeaders {
    const selectedCompany = this.companyService.selectedCompany$.value;
    let headers = new HttpHeaders();


    if (selectedCompany?.id) {
      headers = headers.set('X-Company-Id', selectedCompany.id.toString());
    }

    return headers;
  }

  private buildQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return httpParams;
  }
}
